import React, { useEffect, useState } from 'react';
import { APP_STORE_URL, ICON_CROSSING } from './index';

/**
 * Nav — stay out of the way, keep a download one tap away.
 *
 * No nav links. One page, one action; the five-anchor menu the old site
 * carried has no job on a single scroll.
 *
 * Transparent over the hero at scrollY 0, gaining its blur and hairline
 * after 24px of scroll. That is a real event, so the transition is permitted
 * motion.
 */
export function Nav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      aria-label="Primary"
      data-scrolled={scrolled}
      className={`fixed inset-x-0 top-0 z-50 h-14 transition-[background-color,border-color,backdrop-filter] duration-300 lg:h-[72px] ${
        scrolled
          ? 'border-b border-[color:var(--cp-hairline)] backdrop-blur-[18px] backdrop-saturate-[1.4]'
          : 'border-b border-transparent'
      }`}
      style={{
        backgroundColor: scrolled ? 'rgba(5, 5, 14, 0.72)' : 'transparent',
      }}
    >
      {/* Scrim for the transparent-at-top state, below lg only.
          768 is the one width where the hero globe runs full-bleed under a
          transparent nav AND the wordmark is visible — it is hidden under
          480, and at 1440 the globe is capped and pushed right so it never
          reaches the wordmark. There, paper at 14px over a bright dot core
          measured 4.36:1. Scrimming the nav rather than the globe fixes it
          without spending any more of the hero's spectacle, which is already
          heavily scrimmed. Once scrolled, the nav's own backdrop takes over
          and this is redundant, so it fades out. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 top-0 -z-10 h-[140%] transition-opacity duration-300 lg:hidden ${
          scrolled ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          background:
            'linear-gradient(to bottom, rgba(5,5,14,0.92) 0%, rgba(5,5,14,0.72) 55%, rgba(5,5,14,0) 100%)',
        }}
      />

      {/* First focusable element on the page. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-5 focus:top-3 focus:z-50 focus:rounded-full focus:bg-gold-400 focus:px-5 focus:py-2 focus:text-[13px] focus:font-bold focus:text-canvas"
      >
        Skip to content
      </a>

      <div className="mx-auto flex h-full w-full max-w-[1200px] items-center justify-between px-5 md:px-10 xl:px-16">
        <a href="/" className="flex items-center gap-3" aria-label="CrossedPaths home">
          <img
            src={ICON_CROSSING.src96}
            alt=""
            width={28}
            height={28}
            decoding="async"
            className="h-7 w-7 rounded-[7px]"
          />
          <span className="hidden font-mono text-[14px] lowercase tracking-[0.06em] text-paper min-[480px]:inline">
            crossedpaths.ai
          </span>
        </a>

        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-download"
          aria-label="Download CrossedPaths on the App Store"
          className="inline-flex items-center justify-center rounded-full px-[18px] py-[10px] text-[13px] font-bold text-canvas transition-[box-shadow,transform] duration-150 ease-reveal hover:-translate-y-px hover:shadow-[0_10px_40px_rgba(255,197,61,0.45)] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-gold-300"
          style={{
            fontFamily: 'var(--font-body)',
            background: 'linear-gradient(135deg, #FFC53D 0%, #D9A226 100%)',
            boxShadow: '0 8px 32px rgba(255, 197, 61, 0.28)',
          }}
        >
          Download
        </a>
      </div>
    </nav>
  );
}

export default Nav;
